import { useState, useEffect } from 'react';

export interface PageConfigModel {
    width: number,
    height: number,
    portrait: boolean,
    color: boolean,
    dpi: number,
    margins: {
        top: number,
        bottom: number,
        left: number,
        right: number,
        isMirrored: boolean
    },
    textAlignment: string,
    font: {
        name: string,
        size: number,
        lineSpacing: number
    }
};

export interface UpdateMargin {
    name: string,
    setting: PageConfigModel
}

interface ConfigModel {
    id: string,
    createdAt: string,
    modifiedAt: string,
    owner: string,
    name: string,
    preferences: {
      background: boolean,
      compress: boolean,
      copies: number,
      encoding: string,
      page: PageConfigModel,
      image: {
        dpi: number,
        quality: number
      }
    }
  }

interface UseAppConfigResult {
  config: ConfigModel | null;
  loading: boolean;
  error: string | null;
}

// Helper function para obtener la URL de la API
const getApiUrl = (): string => {
  const runtimeApiUrl = (window as any).ENV?.REACT_APP_API_URL;
  const buildTimeApiUrl = process.env.REACT_APP_API_URL;
  const configuredUrl = runtimeApiUrl || buildTimeApiUrl;
  
  // Si está configurada una URL externa, usar proxy local para evitar CORS
  if (configuredUrl && configuredUrl !== 'http://localhost:5266') {
    return '/api'; // Usar el proxy nginx
  }
  
  return configuredUrl || 'http://localhost:5266';
};

export const getConfig = (appId: string, clientKey: string, modelId: string): UseAppConfigResult => {
  const [config, setConfig] = useState<ConfigModel | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const fetchConfig = async () => {
    if (!appId) {
      setConfig(null);
      setError(null);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // URL del endpoint - primero intenta runtime config, luego build-time env, luego fallback
      const runtimeApiUrl = (window as any).ENV?.REACT_APP_API_URL;
      const buildTimeApiUrl = process.env.REACT_APP_API_URL;
      const apiUrl = runtimeApiUrl || buildTimeApiUrl || 'http://localhost:5266';
      const endpoint = `${apiUrl}/Model/${modelId}`;

      console.log('=== DEBUG FETCH ===');
      console.log('API URL:', apiUrl);
      console.log('Endpoint:', endpoint);
      console.log('Headers que se enviarán:', {
        'Content-Type': 'application/json',
        'X-OWNER': appId,
        'X-API-KEY': clientKey
      });

      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        'X-OWNER': appId,
        'X-API-KEY': clientKey
      };

      console.log('Iniciando fetch...');
      const response = await fetch(endpoint, {
        method: 'GET',
        headers,
      });

      console.log('Response status:', response.status);
      console.log('Response ok:', response.ok);

      if (!response.ok) {
        throw new Error(`Error ${response.status}: ${response.statusText}`);
      }

      const data: ConfigModel = await response.json();
      
      // Validar que la respuesta tenga la estructura esperada
      if (!data.id || !data.preferences) {
        throw new Error('Respuesta del servidor inválida');
      }

      setConfig(data);
      console.log('Config loaded successfully:', data);

    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Error desconocido';
      setError(errorMessage);
      console.error('Error fetching config:', err);
      
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchConfig();
  }, [appId, clientKey, modelId]);

  return { config, loading, error };
};

export const updateConfig = async (appId: string, clientKey: string, modelId: string, setting: UpdateMargin): Promise<ConfigModel | null> => {
  console.log('Updating config with:', setting);
  
  if (!appId) {
    throw new Error('AppId is required');
  }

  try {
    const runtimeApiUrl = (window as any).ENV?.REACT_APP_API_URL;
    const buildTimeApiUrl = process.env.REACT_APP_API_URL;
    const apiUrl = runtimeApiUrl || buildTimeApiUrl || 'http://localhost:5266';
    const endpoint = `${apiUrl}/Model/${modelId}`;

    console.log('Updating config at:', endpoint);
    console.log('Using API URL:', apiUrl);

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'X-OWNER': appId,
      'X-API-KEY': clientKey
    };

    const response = await fetch(endpoint, {
      method: 'PATCH',
      headers,
      body: JSON.stringify(setting),
    });

    if (!response.ok) {
      throw new Error(`Error ${response.status}: ${response.statusText}`);
    }

    const data: ConfigModel = await response.json();
    console.log('Config updated successfully:', data);
    
    return data;

  } catch (err) {
    const errorMessage = err instanceof Error ? err.message : 'Error desconocido';
    console.error('Error updating config:', err);
    throw new Error(errorMessage);
  }
};

export type { ConfigModel as AppConfigModel };
